/**
 * Offline store — bruker IndexedDB (via idb-keyval) til å cache siste API-svar
 * og til å kø mutasjoner (POST/PUT/DELETE) mens brukaren er offline. Når
 * nettverket kjem tilbake, spyler vi køen automatisk (via `flushQueue`).
 *
 * IndexedDB gir oss praktisk sett ubegrensa lagring (typisk hundrevis av MB),
 * i motsetning til localStorage sin 5MB-grense.
 *
 *   - Cache scope-nøkler: pantry, shopping-list (per week/shift/list)
 *   - Kø-nøkkel: pending_mutations = [{ id, method, url, data, timestamp, description }]
 *   - Konfliktløysing: last-write-wins (server-side)
 */
import { get as idbGet, set as idbSet, del as idbDel, createStore } from "idb-keyval";

// Separate DBs for cache og queue. idb-keyval sin createStore åpner databasen
// første gang og legger berre til den navngitte store — så ein DB per store.
const CACHE_STORE = createStore("pb-offline-cache", "cache");
const QUEUE_STORE = createStore("pb-offline-queue", "queue");
const QUEUE_KEY = "pending_mutations";
const TTL_MS = 7 * 24 * 3600 * 1000; // 7 dager

// In-memory shadow of the queue so we can dispatch synkrone "queue-changed"
// events uten å vente på ein IDB read.
let _queueShadow = null;

async function _loadQueueShadow() {
  if (_queueShadow !== null) return _queueShadow;
  try {
    _queueShadow = (await idbGet(QUEUE_KEY, QUEUE_STORE)) || [];
  } catch {
    _queueShadow = [];
  }
  return _queueShadow;
}
// Prewarm shadow ved modul-lasting
_loadQueueShadow();

// Rydd opp gamle DB frå tidlegare versjon (pre-refactor)
try {
  indexedDB.deleteDatabase("pb-offline");
} catch { /* ignore */ }

// ─── Cache (siste kjente server-tilstand) ─────────────────────────────
export async function cacheGet(scope) {
  try {
    const record = await idbGet(scope, CACHE_STORE);
    if (!record) return null;
    if (Date.now() - record.savedAt > TTL_MS) {
      await idbDel(scope, CACHE_STORE);
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

export function cacheGetSync(scope) {
  // Legacy sync accessor for interceptor hot-path: må gå asynkront.
  // Vi held ein liten LRU shadow så vi kan returne umiddelbart.
  return _cacheShadow[scope] || null;
}

const _cacheShadow = {};

export async function cacheSet(scope, data) {
  const record = { data, savedAt: Date.now() };
  _cacheShadow[scope] = record;
  try {
    await idbSet(scope, record, CACHE_STORE);
  } catch { /* quota exceeded, ignore */ }
}

export async function cacheClear(scope) {
  delete _cacheShadow[scope];
  try {
    await idbDel(scope, CACHE_STORE);
  } catch { /* ignore */ }
}

/**
 * Muter cache-verdien for eit scope in-place. `mutator(data)` kan returnere
 * ny data (immutable-style) eller mutere `data` og returnere den. Ny data
 * blir lagra tilbake i IDB. Om scope ikkje finnes i cache, gjer vi ingenting.
 *
 * Brukast for optimistisk oppdatering ved offline-mutasjonar, slik at
 * brukaren umiddelbart ser eiga endring i lista utan å måtte vente på
 * server-svar.
 */
export async function cacheMutate(scope, mutator) {
  // Sync-shadow først (dersom vi har den), fall back til IDB
  const record = _cacheShadow[scope] || (await cacheGet(scope));
  if (!record || !Array.isArray(record.data) && typeof record.data !== "object") return;
  let nextData;
  try {
    nextData = mutator(record.data);
  } catch (e) {
    console.warn("[offline] cacheMutate: mutator threw", e);
    return;
  }
  if (nextData === undefined) return; // signalise "ingen endring"
  const updated = { data: nextData, savedAt: record.savedAt || Date.now() };
  _cacheShadow[scope] = updated;
  try {
    await idbSet(scope, updated, CACHE_STORE);
  } catch { /* ignore */ }
}

// ─── Sync-kø (offline mutasjonar) ─────────────────────────────────────
export function getQueue() {
  // Sync accessor — bruker in-memory shadow. Shadow er prewarmed ved
  // modul-lasting så første gang etter appstart er den også korrekt.
  return _queueShadow || [];
}

export async function getQueueAsync() {
  return await _loadQueueShadow();
}

async function saveQueue(q) {
  _queueShadow = q;
  try {
    await idbSet(QUEUE_KEY, q, QUEUE_STORE);
    window.dispatchEvent(new CustomEvent("pb-queue-changed", { detail: { count: q.length } }));
  } catch { /* ignore */ }
}

export async function enqueueMutation(mut) {
  const q = await _loadQueueShadow();
  q.push({ ...mut, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, timestamp: Date.now() });
  await saveQueue(q);
}

/**
 * Flush kø av ventande mutasjonar. Kaller `sender({method, url, data})` for
 * kvar entry. Om ein feilar (unntak eller 5xx/network), stopper vi og
 * behalder resten i køen — de blir prøvd på neste flush.
 *
 * Return: { flushed: number, remaining: number, errors: [] }
 */
export async function flushQueue(sender) {
  const q = await _loadQueueShadow();
  if (q.length === 0) return { flushed: 0, remaining: 0, errors: [] };
  const errors = [];
  let flushed = 0;
  while (q.length > 0) {
    const item = q[0];
    try {
      await sender(item);
      q.shift();
      flushed++;
      await saveQueue(q);
    } catch (err) {
      const status = err.response?.status;
      if (!status) {
        errors.push({ item, error: "network" });
        return { flushed, remaining: q.length, errors };
      }
      errors.push({ item, error: `${status} ${err.response?.data?.detail || ""}` });
      q.shift();
      await saveQueue(q);
    }
  }
  return { flushed, remaining: 0, errors };
}
